import { Injectable } from '@angular/core';
import {HttpClient, HttpParams} from '@angular/common/http';
import * as moment from 'moment';
import { map, catchError, tap } from 'rxjs/operators';
import { Observable, throwError, of, Subject, EMPTY, forkJoin } from 'rxjs';
import { JsonMapperService } from 'src/app/core/services/mapper/mapper.service';
import { DeclarationRecolte } from '../../models/declaration-recolte.model';
import { ProduitsService } from 'src/app/data/habilitation/services/produits/produits.service';
import { DREVSaisie } from '../../models/drevsaisie.model';
import { Declarations } from '../../models/declarations.model';
import { Entreprise } from 'src/app/data/intervenant/models/entreprise.model';
import { DeclarationIgp } from '../../models/declaration-igp.model';
import { DeclarationExport } from '../../models/declaration-export.model';
import { OperateursService } from 'src/app/data/intervenant/services/operateurs/operateurs.service';
import { ReferentielService } from '../referentiel/referentiel.service';
import { SuiviDrDrev } from '../../models/suivi-dr-drev.model';
import { SyntheseDrev } from "../../models/synthese-drev.model";
import { DeclarationExportRequest } from '../../models/declaration-export-request.model';
import {DeclarationSurfaceEntreprise} from "../../models/declaration-surface-entreprise.model";

@Injectable({
    providedIn: 'root'
})
export class DeclarationsService {
    constructor(
        private readonly http: HttpClient,
        private readonly mapper: JsonMapperService,
        private readonly produitsService: ProduitsService,
        private readonly operateurService: OperateursService,
        private readonly referentielService: ReferentielService
    ) {}

    activeTabId: string;
    entrepriseSelectionnee: Entreprise;
    setActiveTabId(id: string): void {
        this.activeTabId = id;
    }

    getActiveTabId(): string {
        return this.activeTabId;
    }

    getDeclarationsRecolte(idIntervenant: number, annee: string): Observable<DeclarationRecolte> {
        // return this.http.get<object[]>(`/assets/mocks/declarations-dr.json`).pipe(
        return this.http.get<object>(`/api/declaration/private/intervenant/${idIntervenant}/dr/${annee}`).pipe(
            map(response => this.mapper.deserializeObject(response, DeclarationRecolte)),
            tap(declaration => {
                of([].concat(declaration.drs, declaration.sv11s, declaration.sv12s, declaration.dpcaves, declaration.dpnegos, declaration.dprecoltants))
                    .pipe(this.produitsService.getProduitPipe())
                    .subscribe();
            })
        );
    }

    getDeclarationsRevendication(idIntervenant: number, annee: string): Observable<Declarations> {
        // return this.http.get<object[]>(`/assets/mocks/declarations-drev.json`).pipe(
        return this.http.get<object[]>(`/api/declaration/private/intervenant/${idIntervenant}/drev/new/${annee}`).pipe(
            map(response => this.mapper.deserializeObject(response, Declarations)),
            tap(response => {
                // Récupérer les produits liés aux déclarations AOC
                response.aocs.forEach(aoc => {
                    this.produitsService.getProduitByCode(aoc.codeProduit).subscribe(produit => (aoc.produit = produit));
                });
                // Récupérer les produits liés aux déclarations IGP
                response.igps.forEach(igp => {
                    this.produitsService.getProduitByCode(igp.codeProduit).subscribe(produit => (igp.produit = produit));
                });
            })
        );
    }

    getConsultationDREV(idIntervenant: number, annee: string): Observable<Declarations> {
        // return this.http.get<object[]>(`/assets/mocks/declarations-drev.json`).pipe(
        return forkJoin(
            this.http.get<object[]>(`/api/declaration/private/intervenant/${idIntervenant}/drev/annee/${annee}`),
            this.referentielService.getReferentiel()
        ).pipe(
            map(([response, ref]) => this.mapper.deserializeObject(response, Declarations, ref)),
            tap(response => {
                // Récupérer les produits liés aux déclarations AOC
                response.aocs.forEach(aoc => {
                    this.produitsService.getProduitByCode(aoc.codeProduit).subscribe(produit => (aoc.produit = produit));
                });
                // Récupérer les produits liés aux déclarations IGP
                response.igps.forEach(igp => {
                    this.produitsService.getProduitByCode(igp.codeProduit).subscribe(produit => (igp.produit = produit));
                });
            })
        );
    }

    getConsultationDrevVCI(idIntervenant: number): Observable<Declarations> {
        // return this.http.get<object[]>(`/assets/mocks/declarations-drev.json`).pipe(

        return forkJoin(
            this.http.get<object[]>(`/api/declaration/private/intervenant/${idIntervenant}/drev/vci`),
            this.referentielService.getReferentiel()
        ).pipe(
            map(([response, ref]) => this.mapper.deserializeObject(response, Declarations, ref)),
            tap(response => {
                // Récupérer les produits liés aux déclarations AOC
                response.aocs.forEach(aoc => {
                    this.produitsService.getProduitByCode(aoc.codeProduit).subscribe(produit => (aoc.produit = produit));
                });
            })
        );
    }

    saisieStockDeclarationsRevendications(saisies: DREVSaisie[], idIntervenant: number, annee: number): Observable<void> {
        // saisies = saisies.filter(saisie => saisie.vciComplement || saisie.vciRemplacement || saisie.vciSubstitution);
        return this.http.patch(`/api/declaration/private/intervenant/${idIntervenant}/drev/new/${annee}`, this.mapper.serialize(saisies)).pipe(map(() => {}));
    }

    getInventaireVergerDefinitiveStatus(idEntreprise: number, idCampagne: number): Observable<boolean> {
        // eslint-disable-next-line max-len
        return this.http.get<any>(`/api/declaration/private/entreprises/${idEntreprise}/campagnes/${idCampagne}/inventaireVergerDefinitiveStatut`);
    }

    getDrevIgp(idIntervenant: number, annee: string, idProduit: number): Observable<DeclarationIgp[]> {
        // eslint-disable-next-line max-len
        return this.http.get<DeclarationIgp[]>(`/api/declaration/private/intervenant/${idIntervenant}/produits/${idProduit}/drevigp/annee/${annee}`).pipe(
            map(response => this.mapper.deserializeArray(response, DeclarationIgp)),
            tap(response => {
                // Récupérer les produits liés aux déclarations IGP
                response.forEach(igp => {
                    this.produitsService.getProduitByCode(igp.codeProduit).subscribe(produit => (igp.produit = produit));
                });
            })
        );
    }

    updateDrevIgp(declarationsIgp: DeclarationIgp[], idIntervenant: number): Observable<void> {
        return this.http.patch(`/api/declaration/private/intervenant/${idIntervenant}/drevigp`, this.mapper.serialize(declarationsIgp)).pipe(map(() => {}));
    }

    getDeclarationExport(request: DeclarationExportRequest): Observable<DeclarationExport[]> {
        return this.http
            .post<DeclarationExport[]>('/api/declaration/private/declaration-export', request)
            .pipe(map(response => this.mapper.deserializeArray(response, DeclarationExport)));
    }

  /**
   * Récupère le nombre de déclarations douane dp (cave, nego et recoltant)
   * @param idOrganisme - Id organisme
   * @param annee - Campagne en cours
   */
  nbDeclarationDouaneDp(idOrganisme: number, annee: string): Observable<number> {
    return this.http.get<number>(`/api/declaration/private/organisme/${idOrganisme}/douane-dp/${annee}`);
  }

  /**
   * Récupère le nombre de déclarations douane dp modifiées (cave, nego et recoltant)
   * @param idOrganisme - Id organisme
   * @param annee - Campagne en cours
   */
  nbDeclarationDouaneDpModify(idOrganisme: number, annee: string): Observable<number> {
    return this.http.get<number>(`/api/declaration/private/organisme/${idOrganisme}/douane-dp-modify/${annee}`);
  }

  /**
   * Récupère le nombre de DREV AOC
   * @param idOrganisme - Id organisme
   * @param annee - Campagne en cours
   */
  nbDrevAOC(idOrganisme: number, annee: string): Observable<number> {
    return this.http.get<number>(`/api/declaration/private/organisme/${idOrganisme}/drev-aoc/${annee}`);
  }

  /**
   * Récupère les suivis des DR et DREV
   * @param idOrganisme - Id organisme
   * @param annee - Campagne
   * @param raisonSociale - Raison sociale
   * @param withDR - Avec date de DR
   * @param typeDR - Tous ou Initiale ou Modifiée
   * @param withDREV - Avec date de DREV
   * @param dateDebutDepotDR - Date début de dépot DR
   * @param dateFinDepotDR - Date fin de dépot DR
   * @param dateDebutSaisieDREV - Date début de saisie DREV
   * @param dateFinSaisieDREV - Date fin de saisie DREV
   */
  getSuiviDrDrev(
    idOrganisme: number,
    annee: string,
    raisonSociale: string,
    withDR: boolean,
    typeDR: string,
    withDREV: boolean,
    dateDebutDepotDR: moment.Moment,
    dateFinDepotDR: moment.Moment,
    dateDebutSaisieDREV: moment.Moment,
    dateFinSaisieDREV: moment.Moment,
  ): Observable<SuiviDrDrev[]> {
    let params = new HttpParams();
    params = raisonSociale !== null ? params.set('raisonSociale', raisonSociale) : params;
    params = withDR !== null ? params.set('withDR', withDR.toString()) : params;
    params = typeDR !== null ? params.set('typeDR', typeDR) : params;
    params = withDREV !== null ? params.set('withDREV', withDREV.toString()) : params;
    params = dateDebutDepotDR !== null ? params.set('dateDebutDepotDR', dateDebutDepotDR.format('YYYY-MM-DD')) : params;
    params = dateFinDepotDR !== null ? params.set('dateFinDepotDR', dateFinDepotDR.format('YYYY-MM-DD')) : params;
    params = dateDebutSaisieDREV !== null ? params.set('dateDebutSaisieDREV', dateDebutSaisieDREV.format('YYYY-MM-DD')) : params;
    params = dateFinSaisieDREV !== null ? params.set('dateFinSaisieDREV', dateFinSaisieDREV.format('YYYY-MM-DD')) : params;
    return this.http.get<SuiviDrDrev[]>(`/api/declaration/private/organisme/${idOrganisme}/campagne/${annee}/suivi-dr-drev`, { params });
  }

  /**
   * Récupère la synthèse des DREV par cahier des charges
   * @param idOrganisme - Id organisme
   * @param annee - Campagne
   */
  getSyntheseDrevByCahier(idOrganisme: number, annee: string): Observable<SyntheseDrev[]> {
    return this.http.get<SyntheseDrev[]>(`/api/declaration/private/organisme/${idOrganisme}/campagne/${annee}/synthese-drev-cahier`);
  }

  /**
   * Récupère la synthèse des DREV par produit
   * @param idOrganisme - Id organisme
   * @param annee - Campagne
   */
  getSyntheseDrevByProduit(idOrganisme: number, annee: string): Observable<SyntheseDrev[]> {
    return this.http.get<SyntheseDrev[]>(`/api/declaration/private/organisme/${idOrganisme}/campagne/${annee}/synthese-drev-produit`);
  }

  /**
   * Récupère la surface par entreprise
   */
  getSurfaceByEntreprise(): Observable<DeclarationSurfaceEntreprise[]> {
    return this.http.get<DeclarationSurfaceEntreprise[]>(`/api/declaration/private/declaration-surface-entreprise`);
  }
}
